import {
  PROPERTY_LOAN_PROCESSING_FEE_LABEL,
  propertyLoanProcessingFee,
  propertyLoanNetDisbursement,
} from "../../utils/propertyLoanProcessingFee";
import { repaymentStatusLabel } from "../../utils/repaymentStatus";
import { getMyInfoData } from "../../utils/myinfoData";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import {
  Modal,
  Button,
  Timeline,
  Input,
  message,
  Tabs,
} from "antd";
import { useMediaQuery } from "@mui/material";
import pdfToText from "react-pdftotext";
import "../../assets/css/app.css";
import "../../borrower/loan/LoanHubCorporate.css";
import "./PropertyTermLoan.css";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  createTrackingId,
  getDeviceType,
  trackUserProductInterest,
} from "../../utils/userProductInterest";

import { propertyLoanMonths, propertyLoanName, propertyLoanTenureLabel, halfCaveatLtv } from "../../utils/propertyLoanProducts";
import PropertyLoanApplication from "./PropertyLoanApplication";
import { propertyMarketplaceLabel } from "../../utils/propertyLoanMarketplace";
import usePropertyMarketplaceRefresh from "../../hooks/usePropertyMarketplaceRefresh";
import PropertyLoanFaq from "./PropertyLoanFaq";
import PropertyLoanEmptyState from "./PropertyLoanEmptyState";
import "./PropertyLoanRedesign.css";
import { buildPropertyLoanRepaymentSchedule } from "../../utils/propertyLoanRepayments";

import { propertyOwnershipError, withPropertyOwnershipDetails } from "../../utils/propertyLoanOwnership";

import usePropertyApplicationProgress from "../../hooks/usePropertyApplicationProgress";
import { applicationSnapshot, PROPERTY_APPLICATION_STEPS } from "../../utils/propertyApplicationProgress";

const { TabPane } = Tabs;

const createEmptyPropertyLoanForm = () => ({
    property: null,
    addressShownData: null,
    propertyOwnership: "",
    propertyShare: null,
    otherOwners: [],
    applicantCpfUsage: 0,
    cpfUsage: 0,
    accruedInterest: 0,
    valuation: null,
    propertyType: null,
    oustandingLoan: 0,
    loanAmount: 0,
    originalLoanOutstanding: 0,
    originalcpfUsage: 0,
    validationNeeded: false,
    documentForLoanOustanding: [],
    documentForCpfUsage: [],
    cbsReport: null,
    loanPaymentType: "",
    selectedLoanType: 0,
    loanRepaymentSchedule: [],
  });

const OFFER_STATUS_COPY = {
  pending_borrower_action: {
    label: "Pending your response",
    color: "gold",
  },
  borrower_renegotiation: {
    label: "Waiting on lender revision",
    color: "blue",
  },
  borrower_rejected: {
    label: "Rejected",
    color: "red",
  },
  awaiting_lender_documents: {
    label: "Awaiting lender documents",
    color: "blue",
  },
  pending_borrower_signature: {
    label: "Documents ready for signature",
    color: "green",
  },
  pending_lender_confirmation: {
    label: "Awaiting lender confirmation",
    color: "cyan",
  },
  revision_requested: {
    label: "Changes requested by lender",
    color: "orange",
  },
  pending_disbursement: {
    label: "Pending disbursement of funds",
    color: "geekblue",
  },
  ongoing: { label: "Ongoing", color: "green" },
};

const OFFER_ACTION_COPY = {
  accept: {
    title: "Accept offer",
    okText: "Accept offer",
    description:
      "Accepting confirms you agree with the lender's terms. The lender will be notified to prepare the agreement documents.",
  },
  renegotiate: {
    title: "Request adjustments",
    okText: "Send request",
    description:
      "Tell the lender what needs to change so they can revise the offer.",
  },
  reject: {
    title: "Reject offer",
    okText: "Reject offer",
    description:
      "Rejecting removes this offer from consideration. You can leave an optional note for the lender.",
  },
};

const HISTORY_ACTION_COPY = {
  offer_created: "Offer created",
  offer_updated: "Offer updated",
  accepted_offer: "Offer accepted",
  rejected_offer: "Offer rejected",
  requested_renegotiation: "Renegotiation requested",
  uploaded_initial_documents: "Lender uploaded documents",
  uploaded_signed_documents: "Signed documents uploaded",
  reuploaded_documents: "Updated documents uploaded",
  documents_confirmed: "Lender confirmed documents",
  revision_requested: "Lender requested document changes",
  uploaded_document: "Document uploaded",
  borrower_confirmed_documents: "You confirmed documents are ready",
};

const HISTORY_COLOR_MAP = {
  lender: "blue",
  borrower: "green",
  system: "gray",
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }

  return numeric.toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
    minimumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }

  return `${numeric}% p.a.`;
};

const formatYears = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }

  return `${numeric} year${numeric === 1 ? "" : "s"}`;
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toTitleCase = (value = "") =>
  value
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getStatusDisplay = (status) => {
  if (!status) return { label: "", color: "default" };
  const config = OFFER_STATUS_COPY[status];
  if (config) return config;
  return {
    label: toTitleCase(status.replace(/_/g, " ")),
    color: "default",
  };
};

const getPillVariant = (color) => {
  if (color === "red") return "danger";
  if (color === "green") return "success";
  if (color === "gold" || color === "orange") return "warning";
  return "info";
};

const getHistoryActionLabel = (action) =>
  HISTORY_ACTION_COPY[action] || toTitleCase((action || "").replace(/_/g, " "));

const getActorLabel = (actor) => {
  if (actor === "borrower") return "You";
  if (actor === "lender") return "Lender";
  return "System";
};

const extractTextBetween = (text, beforeText, afterText) => {
  if (!text) return "";
  const beforeTextPattern = beforeText.replace(/\s+/g, "\\s+");
  const afterTextPattern = afterText.replace(/\s+/g, "\\s+");
  const regex = new RegExp(`${beforeTextPattern}(.*?)${afterTextPattern}`);
  const match = text.match(regex);
  return match && match[1] ? match[1].trim() : "";
};

const parseCbsData = (text) => {
  if (!text) return null;
  const securedCreditLimit = extractTextBetween(
    text,
    "Secured Credit Limit",
    "Unsecured Credit Limit",
  );
  const unsecuredCreditLimit = extractTextBetween(
    text,
    "Unsecured Credit Limit",
    "Exempted Credit Limit",
  );
  const creditScore = extractTextBetween(
    text,
    "financial institution.  Score",
    "Risk Grade",
  );
  const riskGrade = extractTextBetween(
    text,
    "Risk Grade ",
    "Risk Grade Description",
  );
  const probabilityDefault = extractTextBetween(
    text,
    "Probability of Default ",
    " For more details on the description",
  );
  return {
    secured_credit_limit: securedCreditLimit || null,
    unsecured_credit_limit: unsecuredCreditLimit || null,
    credit_score: creditScore || null,
    risk_grade: riskGrade || null,
    probability_default: probabilityDefault || null,
  };
};

const PropertyTermLoan = () => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const trackingRef = useRef({
    trackingId: createTrackingId(),
    enteredLoanAmount: false,
    enteredInterestRate: false,
    enteredTenor: false,
    loanAmount: null,
  });
  const sendTrackingUpdate = (updates = {}) => {
    trackingRef.current = { ...trackingRef.current, ...updates };
    trackUserProductInterest({
      trackingId: trackingRef.current.trackingId,
      productPage: "b-instant-prop-term-loan",
      deviceType: getDeviceType(isMobile),
      enteredLoanAmount: trackingRef.current.enteredLoanAmount,
      enteredInterestRate: trackingRef.current.enteredInterestRate,
      enteredTenor: trackingRef.current.enteredTenor,
      loanAmount: trackingRef.current.loanAmount,
    });
  };
  const applicationPageRef = useRef(null);
  const [submissionSteps, setSubmissionSteps] = useState(0);
  const [propTermRequest, setPropTermRequest] = useState([]);
  const [requestSelected, setRequestSelected] = useState(null);
  useEffect(() => {
    sendTrackingUpdate();
  }, []);

  const typesOfLoans = [
    {
      loanName: "First Charge Equity Term Loan",
      sub: [
        { value: "Refinancing Outstanding Loan from Current Financier" },
        { value: "Interest Range between 6 - 10% p.a." },
        { value: "Tenor up to 25 years regardless of Age" },
        { value: "LTV up to 85% for landed property; 80% for other property" },
        { value: "Processing fee of 1% applied by lender" },
      ],
    },
    {
      loanName: "Bridging Loan",
      sub: [
        {
          value:
            "No requirement to refinance Outstanding loan from Current Financier",
        },
        { value: "Interest Range between 1.5 - 2.5% per month" },
        { value: "Tenor of 3, 6, 9 or 12 months" },
        { value: "LTV up to 85% for landed property; 80% for other property" },
        { value: "Processing fee of 1% applied by lender" },
      ],
    },
    {
      loanName: "Half Caveat Loan",
      sub: [
        { value: "Secured on your share only" },
        { value: "Co-owners don’t sign or borrow" },
        { value: "1.5–2.5% per month, simple interest" },
        { value: "3, 6, 9 or 12 months, renewable" },
        { value: "Processing fee of 1%" },
      ],
    },
  ];

  const [userDetails, setUserDetails] = useState(null);
  const selectableProperties = useMemo(() => {
    const properties =
      userDetails?.myinfo_data?.cpfhousingwithdrawal?.withdrawaldetails;
    // A postal code is required by the valuation lookup.
    return Array.isArray(properties)
      ? properties.filter((property) => property?.address?.postal?.value)
      : [];
  }, [userDetails]);
  const [myinfoNotification, setMyinfoNotification] = useState(0);
  const [findMyHomeValuationForm, setFindMyHomeValuaionForm] = useState(createEmptyPropertyLoanForm);
  const [loadingResults, setLoadingResults] = useState(false);
  const [valuationStatus, setValuationStatus] = useState("not_started");
  const valuationSearchRef = useRef(0);
  const [submissionStatus, setSubmissionStatus] = useState("not_started");
  const [cbsUploadedDocument, setCbsUploadedDocument] = useState(null);
  const [selectedLoanType, setSelectedLoanType] = useState(null);

  const [agreed, setAgreed] = useState(false);
  const [possibleResults, setPossibleResults] = useState(0);
  const [offerActionModal, setOfferActionModal] = useState({
    open: false,
    action: null,
    offer: null,
  });
  const [offerActionMessage, setOfferActionMessage] = useState("");
  const [offerActionSubmitting, setOfferActionSubmitting] = useState(false);
  const [uploadingOfferId, setUploadingOfferId] = useState(null);
  const [confirmingOfferId, setConfirmingOfferId] = useState(null);
  const [uploadNotesMap, setUploadNotesMap] = useState({});
  const fileInputRefs = useRef({});
  const [cbsPromoCode, setCbsPromoCode] = useState("");
  const [cbsFiles, setCbsFiles] = useState([]);
  const [, setCbsFileText] = useState("");
  const [cbsExtractedData, setCbsExtractedData] = useState(null);
  const [cbsUploading, setCbsUploading] = useState(false);
  const submissionInFlightRef = useRef(false);
  const [activeTab, setActiveTab] = useState("apply");
  const [repaymentScheduleOffer, setRepaymentScheduleOffer] = useState(null);

  const progress = usePropertyApplicationProgress(
    userDetails?.user_id,
    applicationSnapshot(findMyHomeValuationForm, {
      selectedLoanType, agreed, cbsPromoCode, cbsFiles,
      cbsExtractedData, cbsUploadedDocument, submissionStatus, valuationStatus, valuationResult: possibleResults,
    }),
    PROPERTY_APPLICATION_STEPS[submissionSteps] || "Property valuation",
    !!findMyHomeValuationForm.property || cbsFiles.length > 0 || agreed ||
      !!findMyHomeValuationForm.loanAmount || !!findMyHomeValuationForm.propertyOwnership,
  );

  const updateRepaymentSchedule = (selectedLoanType) => {
    const numberOfMonths = propertyLoanMonths(selectedLoanType, findMyHomeValuationForm);
    const newSchedule = buildPropertyLoanRepaymentSchedule(selectedLoanType, findMyHomeValuationForm);

    setFindMyHomeValuaionForm({
      ...findMyHomeValuationForm,
      selectedLoanType: selectedLoanType,
      loanTenureMonths: numberOfMonths,
      loanRepaymentSchedule: newSchedule,
    });
  };

  const UniversaleUploadFile = async (file, type) => {
    const property = findMyHomeValuationForm.property;
    const amountKey = type === "loan" ? "oustandingLoan" : "cpfUsage";
    const amount = findMyHomeValuationForm[amountKey];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "ploan");
    formData.append("file_id", "");
    const response = await fetch("https://www.smart-lend.com/api/file/uploadUniversalFile", {
      method: "POST",
      headers: { Authorization: `Bearer ${Cookies.get("token")}` },
      body: formData,
    });
    if (!response.ok) throw new Error("Supporting document upload failed.");
    const data = await response.json();
    const key = type === "loan" ? "documentForLoanOustanding" : "documentForCpfUsage";
    setFindMyHomeValuaionForm((current) => {
      if (current.property !== property || current[amountKey] !== amount) return current;
      return { ...current, [key]: [...current[key], { name: data.original_filename, url: data.url }] };
    });
  };

  const uploadPropLoanFile = async (file, fileId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "ploan");
    formData.append("file_id", fileId);

    const response = await fetch(
      "https://www.smart-lend.com/api/file/uploadUniversalFile",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: formData,
      },
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data?.message || "File upload failed.");
    }

    const data = await response.json();
    return {
      document_id: data.document_id,
      file_name: data.original_filename,
      file_url: data.url,
    };
  };

  const fetchCbsPromoCode = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/campaign/getcbscode",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Unable to fetch CBS promo code");
      }
      const json = await response.json();
      setCbsPromoCode(json.cbs_campaign_code || "");
    } catch (error) {
      console.error(error);
      message.error("Unable to retrieve CBS promo code.");
    }
  };

  const fetchuserDetails = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/profile/getUserDetails",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        },
      );
      if (response.status === 200) {
        const json = await response.json();
        const userDetailsToEnter = {
          ...json.User,
          credit_report_data: JSON.parse(json.User.credit_report_data),
          myinfo_data: getMyInfoData(json.User.myinfo_data),
        };

        if (
          !userDetailsToEnter.myinfo_data ||
          !userDetailsToEnter.myinfo_last_sync
        ) {
          setMyinfoNotification(1);
        } else if (userDetailsToEnter.myinfo_last_sync) {
          const lastUpdateDate = new Date(userDetailsToEnter.myinfo_last_sync);
          const currentDate = new Date();
          const oneMonthAgo = new Date();
          oneMonthAgo.setDate(currentDate.getDate() - 30);
          if (lastUpdateDate < oneMonthAgo) {
            setMyinfoNotification(2);
          } else {
            setMyinfoNotification(0);
          }
        }
        console.log(userDetailsToEnter);
        setUserDetails(userDetailsToEnter);

        const privateResidentialLoans =
          userDetailsToEnter.credit_report_data?.NoteworthyOngoingLoans?.filter(
            (tram) => tram?.includes("private residential purchase"),
          );

        if (privateResidentialLoans?.length === 1) {
          setFindMyHomeValuaionForm({
            ...findMyHomeValuationForm,
            oustandingLoan: parseFloat(
              privateResidentialLoans?.[0]
                ?.split("   ")
                [
                  privateResidentialLoans?.[0]?.split("   ").length - 4
                ].replace(/,/g, ""),
            ),
            originalLoanOutstanding: parseFloat(
              privateResidentialLoans?.[0]
                ?.split("   ")
                [
                  privateResidentialLoans?.[0]?.split("   ").length - 4
                ].replace(/,/g, ""),
            ),
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCbsBeforeUpload = async (file) => {
    setCbsUploadedDocument(null);
    setCbsFiles([file]);
    try {
      const text = await pdfToText(file);
      setCbsFileText(text);
      setCbsExtractedData(parseCbsData(text));
    } catch (error) {
      console.error("Failed to extract text from CBS report");
      setCbsFileText("");
      setCbsExtractedData(null);
    }
    return false;
  };

  useEffect(() => {
    fetchuserDetails();
  }, []);

  useEffect(() => {
    if (activeTab === "apply" && submissionSteps === 2) {
      fetchCbsPromoCode();
    }
  }, [activeTab, submissionSteps]);

  const startSeachResults = async (toSearch) => {
    const searchId = ++valuationSearchRef.current;
    // Save the selection before the valuation requests finish.
    setValuationStatus("searching");
    setLoadingResults(true);
    setPossibleResults(0);
    try {
      let item2 = {
        propertyTypeCode: "land",
        postalCode: toSearch.address.postal.value,
        addressUnit: ``,
        addressNumber: "",
        addressStreet: "",
        valuationType: "sale",
        projectName: "",
      };

      const response2 = await fetch(
        `https://www.smart-lend.com/api/property/getPropertyValuation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(item2),
        },
      );
      let item3 = {
        propertyTypeCode: "condo",
        postalCode: toSearch.address.postal.value,
        addressUnit: ``,
        addressNumber: "",
        addressStreet: "",
        valuationType: "sale",
        projectName: "",
      };

      const response3 = await fetch(
        `https://www.smart-lend.com/api/property/getPropertyValuation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(item3),
        },
      );

      let item4 = {
        propertyTypeCode: "office",
        postalCode: toSearch.address.postal.value,
        addressUnit: ``,
        addressNumber: "",
        addressStreet: "",
        valuationType: "sale",
        projectName: "",
      };

      const response4 = await fetch(
        `https://www.smart-lend.com/api/property/getPropertyValuation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(item4),
        },
      );

      if (![response2, response3, response4].every((response) => response.ok)) {
        throw new Error("Unable to retrieve property valuations");
      }
      const json2 = await response2.json();
      const json3 = await response3.json();
      const json4 = await response4.json();

      if (searchId !== valuationSearchRef.current) return;
      const floorUnit = `${toSearch.address.floor?.value || ""}-${toSearch.address.unit?.value || ""}`;
      const matchValuation = (results) => {
        const valuedResults = results.filter((result) => Number(result.saleValuation) > 0);
        const matches = valuedResults.length === 1
          ? valuedResults
          : valuedResults.filter((result) => result.addressUnit === floorUnit);
        return matches.length === 1 ? matches[0] : null;
      };
      const match = matchValuation([
        ...(json2.data || []),
        ...(json3.data || []),
        ...(json4.data || []),
      ]);

      if (match) {
        setPossibleResults({ value: match.saleValuation, type: match.propertyTypeCode });
        setValuationStatus("completed");
      } else {
        // HDB is checked only to explain ineligibility, never to price a loan.
        const hdbResponse = await fetch(
          "https://www.smart-lend.com/api/property/getPropertyValuation",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...item2, propertyTypeCode: "hdb" }),
          },
        );
        if (!hdbResponse.ok) throw new Error("Unable to check property eligibility");
        const hdbResult = await hdbResponse.json();
        if (searchId !== valuationSearchRef.current) return;
        setPossibleResults(0);
        setValuationStatus(matchValuation(hdbResult.data || []) ? "unsupported_hdb" : "completed");
      }
      setLoadingResults(false);
    } catch (error) {
      if (searchId !== valuationSearchRef.current) return;
      setValuationStatus("failed");
      setLoadingResults(false);
      console.error(error);
    }
  };

  const getMyPropLoans = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/propTermLoan/getMyPropLoans",
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + Cookies.get("token"),
            "Content-Type": "application/json", // Add this
          },
        },
      );

      const data = await response.json();

      if (response.status === 200) {
        let dataToPush = [];

        for (const dat of data.data) {
          const toPush = { ...dat };
          toPush.loan_data = typeof dat.loan_data === "string"
            ? JSON.parse(dat.loan_data)
            : (dat.loan_data || {});
          dataToPush.push(toPush);
        }

        setPropTermRequest(dataToPush);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const openOfferActionModal = (action, offer) => {
    setOfferActionModal({ open: true, action, offer });
    setOfferActionMessage("");
  };

  const closeOfferActionModal = () => {
    setOfferActionModal({ open: false, action: null, offer: null });
    setOfferActionMessage("");
    setOfferActionSubmitting(false);
  };

  const handleOfferActionSubmit = async () => {
    const { offer, action } = offerActionModal;
    if (!offer || !action) {
      return;
    }

    const trimmedMessage = offerActionMessage.trim();
    if (action === "renegotiate" && !trimmedMessage) {
      message.warning("Please include a short note for the lender.");
      return;
    }

    try {
      setOfferActionSubmitting(true);
      const response = await fetch(
        `https://www.smart-lend.com/api/propTermLoan/offers/${offer.id}/borrower-response`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: JSON.stringify({
            action,
            message: trimmedMessage || null,
          }),
        },
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || "Unable to update offer.");
      }

      const successCopy =
        action === "accept"
          ? "Offer accepted."
          : action === "reject"
            ? "Offer rejected."
            : "Renegotiation request sent.";

      message.success(successCopy);
      closeOfferActionModal();
      await getMyPropLoans();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Something went wrong.");
    } finally {
      setOfferActionSubmitting(false);
    }
  };

  const handleUploadNotesChange = (offerId, value) => {
    setUploadNotesMap((prev) => ({ ...prev, [offerId]: value }));
  };

  const triggerOfferFileDialog = (offerId) => {
    const input = fileInputRefs.current[offerId];
    if (input) {
      input.click();
    }
  };

  const handleOfferFileChange = (offer, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) {
      handleSignedDocumentUpload(offer, files);
    }
    event.target.value = "";
  };

  const handleSignedDocumentUpload = async (offer, files) => {
    if (!files.length) {
      return;
    }

    const notes = (uploadNotesMap[offer.id] || "").trim();
    let successCount = 0;

    try {
      setUploadingOfferId(offer.id);

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "prop_offer");
        formData.append("file_id", offer.id);

        const uploadResponse = await fetch(
          "https://www.smart-lend.com/api/file/uploadUniversalFile",
          {
            method: "POST",
            headers: {
              Authorization: "Bearer " + Cookies.get("token"),
            },
            body: formData,
          },
        );

        const uploadJson = await uploadResponse.json();
        if (uploadResponse.status !== 200 || !uploadJson?.document_id) {
          throw new Error(uploadJson?.message || "Unable to upload document.");
        }

        const attachResponse = await fetch(
          `https://www.smart-lend.com/api/propTermLoan/offers/${offer.id}/documents`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + Cookies.get("token"),
            },
            body: JSON.stringify({
              document_id: uploadJson.document_id,
              stage: "signed",
              notes: notes || null,
            }),
          },
        );

        const attachJson = await attachResponse.json();
        if (!attachResponse.ok) {
          throw new Error(
            attachJson?.message || "Unable to register document.",
          );
        }

        successCount += 1;
      }

      if (successCount > 0) {
        message.success(
          successCount === 1
            ? "Document uploaded."
            : `${successCount} documents uploaded.`,
        );
        setUploadNotesMap((prev) => ({ ...prev, [offer.id]: "" }));
        await getMyPropLoans();
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to upload document.");
    } finally {
      setUploadingOfferId(null);
    }
  };

  const handleConfirmDocuments = async (offer) => {
    const messageValue = (uploadNotesMap[offer.id] || "").trim();

    try {
      setConfirmingOfferId(offer.id);

      const response = await fetch(
        `https://www.smart-lend.com/api/propTermLoan/offers/${offer.id}/confirm-signed`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            message: messageValue || null,
          }),
        },
      );

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to confirm documents.");
      }

      message.success("Thanks! We’ve notified your lender.");
      setUploadNotesMap((prev) => ({ ...prev, [offer.id]: "" }));
      await getMyPropLoans();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to confirm documents.");
    } finally {
      setConfirmingOfferId(null);
    }
  };

  const renderDocumentSection = (title, documents) => {
    if (!documents || documents.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: "12px" }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: "13px",
            color: "var(--br-text, #142C44)",
          }}
        >
          {title}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            marginTop: "6px",
          }}
        >
          {documents.map((doc) => (
            <a
              key={`doc-${doc.id}`}
              href={doc.file_url}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: "13px", color: "#0A3D62" }}
            >
              {doc.file_name || "View document"}
              {doc.notes ? ` — ${doc.notes}` : ""}
            </a>
          ))}
        </div>
      </div>
    );
  };

  const renderHistoryTimeline = (offer) => {
    const history = Array.isArray(offer.status_history)
      ? [...offer.status_history].reverse()
      : [];

    if (history.length === 0) {
      return null;
    }

    const items = history.map((entry, index) => ({
      color: HISTORY_COLOR_MAP[entry.actor] || "gray",
      children: (
        <div key={`history-${offer.id}-${index}`}>
          <div style={{ fontWeight: 600, fontSize: "13px" }}>
            {getHistoryActionLabel(entry.action)}
          </div>
          {entry.message && (
            <div style={{ marginTop: "4px", fontSize: "13px" }}>
              {entry.message}
            </div>
          )}
          {entry.disbursement_date && (
            <div style={{ marginTop: "4px", fontSize: "12px" }}>
              Disbursement date: {formatDate(entry.disbursement_date)}
            </div>
          )}
          <div
            style={{
              marginTop: "4px",
              fontSize: "11px",
              color: "rgba(20, 44, 68, 0.6)",
            }}
          >
            {getActorLabel(entry.actor)} · {formatDateTime(entry.timestamp)}
          </div>
        </div>
      ),
    }));

    return (
      <div style={{ marginTop: "16px" }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: "13px",
            color: "var(--br-text, #142C44)",
          }}
        >
          Activity
        </div>
        <Timeline style={{ marginTop: "8px" }} items={items} />
      </div>
    );
  };

  const renderOfferCard = (offer) => {
    const offerData = offer.offer_data || {};
    const { label: statusLabel, color: statusColor } = getStatusDisplay(
      offer.status,
    );

    const isActionable = [
      "pending_borrower_action",
      "borrower_renegotiation",
    ].includes(offer.status);

    const canUploadSigned = [
      "pending_borrower_signature",
      "revision_requested",
    ].includes(offer.status);

    const awaitingLenderDocuments =
      offer.status === "awaiting_lender_documents";

    const nextRepayment = offer.next_repayment;
    const lenderDocs = (offer.documents || []).filter(
      (doc) => doc.role === "lender",
    );
    const borrowerDocs = (offer.documents || []).filter(
      (doc) => doc.role === "borrower",
    );

    const noteValue = uploadNotesMap[offer.id] || "";
    const lenderName =
      offer.lender?.entity_name || offer.lender?.principal_name || "Lender";

    return (
      <div
        key={offer.id}
        className={[
          "slCard",
          "loan-hover-card",
          isMobile ? "isMobile" : "",
        ].join(" ")}
        style={{ marginTop: 16, color: "rgba(10, 61, 98, 0.95)" }}
      >
        <div className="slTopRow">
          <div className="slPills">
            <span className={["slPill", getPillVariant(statusColor)].join(" ")}>
              {statusLabel}
            </span>
            <span className="slPill">Offer #{offer.id}</span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "8px",
          }}
        >
          <div>
            <div style={{ fontSize: "14px", fontWeight: 600 }}>
              {lenderName}
            </div>
            <div className="slSubLine">
              Updated {formatDateTime(offer.updated_at)}
            </div>
          </div>
        </div>

        <div className="slMetaGrid" style={{ marginTop: 12 }}>
          <div className="slMetaItem">
            <div className="slMetaK">Loan amount</div>
            <div className="slMetaV">
              {formatCurrency(offerData.amount ?? offerData.amount_offer)}
            </div>
          </div>
          <div className="slMetaItem">
            <div className="slMetaK">Interest rate</div>
            <div className="slMetaV">
              {formatPercent(offerData.rate ?? offerData.rate_pa)}
            </div>
          </div>
          <div className="slMetaItem">
            <div className="slMetaK">Tenor</div>
            <div className="slMetaV">
              {formatYears(offerData.tenor ?? offerData.tenor_years)}
            </div>
          </div>
          <div className="slMetaItem sl-property-fee-detail">
            <div className="slMetaK">Processing fee</div>
            <div className="slMetaV">
              {PROPERTY_LOAN_PROCESSING_FEE_LABEL} ({formatCurrency(propertyLoanProcessingFee(offerData.amount ?? offerData.amount_offer))})
            </div>
          </div>
          <div className="slMetaItem sl-property-fee-detail">
            <div className="slMetaK">Disbursement amount</div>
            <div className="slMetaV">
              {formatCurrency(propertyLoanNetDisbursement(offerData.amount ?? offerData.amount_offer))}
            </div>
          </div>
          <div className="slMetaItem" style={{ gridColumn: "1 / -1" }}>
            <div className="slMetaK">Next repayment</div>
            <div className="slMetaV">
              {nextRepayment
                ? `${formatDate(nextRepayment.due_date)} · ${formatCurrency(
                    nextRepayment.amount_due,
                  )}`
                : "Not scheduled yet"}
            </div>
          </div>
        </div>

        {offer.lender_notes && (
          <div
            style={{
              marginTop: "12px",
              background: "rgba(20, 44, 68, 0.06)",
              padding: "12px",
              borderRadius: "var(--br-card-radius, 8px)",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: 600 }}>Lender note</div>
            <div style={{ fontSize: "13px", marginTop: "4px" }}>
              {offer.lender_notes}
            </div>
          </div>
        )}

        {offer.borrower_notes && (
          <div
            style={{
              marginTop: "12px",
              background: "rgba(10, 61, 98, 0.05)",
              padding: "12px",
              borderRadius: "var(--br-card-radius, 8px)",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: 600 }}>
              Your last note
            </div>
            <div style={{ fontSize: "13px", marginTop: "4px" }}>
              {offer.borrower_notes}
            </div>
          </div>
        )}

        {awaitingLenderDocuments && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px",
              borderRadius: "var(--br-card-radius, 8px)",
              background: "rgba(244, 165, 96, 0.18)",
              color: "#8A4B16",
            }}
          >
            We've notified the lender. They'll upload the agreement documents
            for you to review.
          </div>
        )}

        {renderDocumentSection("Documents from lender", lenderDocs)}
        {renderDocumentSection("Your uploaded documents", borrowerDocs)}

        {canUploadSigned && (
          <div
            style={{
              marginTop: "16px",
              background: "rgba(20, 44, 68, 0.08)",
              padding: "14px",
              borderRadius: "var(--br-card-radius, 8px)",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "13px" }}>
              Upload signed documents
            </div>
            <div style={{ fontSize: "12px", marginTop: "4px" }}>
              Review the lender's documents and upload your signed copies. We'll
              notify the lender immediately.
            </div>
            <Input.TextArea
              rows={2}
              placeholder="Optional note to your lender"
              value={noteValue}
              onChange={(event) =>
                handleUploadNotesChange(offer.id, event.target.value)
              }
              style={{ marginTop: "10px" }}
              maxLength={500}
            />
            <div style={{ marginTop: "10px" }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <Button
                  className="slBtn slBtnPrimary"
                  type="primary"
                  onClick={() => triggerOfferFileDialog(offer.id)}
                  loading={uploadingOfferId === offer.id}
                >
                  {uploadingOfferId === offer.id
                    ? "Uploading..."
                    : "Select files to upload"}
                </Button>
                <Button
                  className="slBtn"
                  onClick={() => handleConfirmDocuments(offer)}
                  loading={confirmingOfferId === offer.id}
                  type="default"
                >
                  {confirmingOfferId === offer.id
                    ? "Sending..."
                    : "Notify lender documents are ready"}
                </Button>
              </div>
              <input
                type="file"
                multiple
                accept="application/pdf,image/*"
                style={{ display: "none" }}
                ref={(element) => {
                  fileInputRefs.current[offer.id] = element;
                }}
                onChange={(event) => handleOfferFileChange(offer, event)}
              />
            </div>
          </div>
        )}

        {isActionable && (
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <Button
              className="slBtn slBtnPrimary"
              type="primary"
              onClick={() => openOfferActionModal("accept", offer)}
            >
              Accept offer
            </Button>
            <Button
              className="slBtn"
              onClick={() => openOfferActionModal("renegotiate", offer)}
            >
              Renegotiate
            </Button>
            <Button
              className="slBtn"
              danger
              onClick={() => openOfferActionModal("reject", offer)}
            >
              Reject
            </Button>
          </div>
        )}

        {renderHistoryTimeline(offer)}
      </div>
    );
  };

  useEffect(() => {
    getMyPropLoans();
  }, []);
  usePropertyMarketplaceRefresh(getMyPropLoans);

  const activeLoan = propTermRequest.find(
    (prop) => prop.id === requestSelected,
  );

  const allOffers = useMemo(
    () =>
      propTermRequest.flatMap((loan) =>
        (loan.offers || []).map((offer) => ({ ...offer, loan })),
      ),
    [propTermRequest],
  );

  const getDisbursementDate = (offer) =>
    offer?.disbursement_date || offer?.offer_data?.disbursement_date || null;

  const isDisbursementReached = (offer) => {
    const disbursementDate = getDisbursementDate(offer);
    if (!disbursementDate) return false;
    const disbursement = new Date(disbursementDate);
    if (Number.isNaN(disbursement.getTime())) return false;
    const today = new Date();
    disbursement.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return today >= disbursement;
  };

  const awaitingResponseStatuses = useMemo(
    () =>
      new Set([
        "pending_lender_confirmation",
        "awaiting_lender_documents",
        "pending_disbursement",
        "pending_borrower_signature",
        "revision_requested",
      ]),
    [],
  );

  const ongoingOffers = useMemo(
    () => allOffers.filter((offer) =>
      ["ongoing", "funded"].includes(offer.status) || Boolean(getDisbursementDate(offer))
    ),
    [allOffers],
  );

  const awaitingResponseOffers = useMemo(
    () =>
      allOffers.filter(
        (offer) =>
          awaitingResponseStatuses.has(offer.status) &&
          !getDisbursementDate(offer),
      ),
    [allOffers, awaitingResponseStatuses],
  );

  const createPropLoan = async () => {
    if (submissionInFlightRef.current) return;
    if (myinfoNotification !== 0) {
      message.warning(
        "Please sync your MyInfo data (within the last 30 days) before submitting.",
      );
      return;
    }
    const ownershipError = propertyOwnershipError(findMyHomeValuationForm);
    if (ownershipError) {
      message.warning(ownershipError);
      return;
    }
    if (!cbsFiles.length) {
      message.warning("Please upload your CBS report before submitting.");
      return;
    }

    submissionInFlightRef.current = true;
    setSubmissionStatus("submitting");
    setCbsUploading(true);
    try {
      const fileId = `prop_term_loan:${userDetails?.user_id || "draft"}`;
      const cbsDocument = await uploadPropLoanFile(cbsFiles[0], fileId);
      setCbsUploadedDocument(cbsDocument);
      const loanPayload = {
        ...withPropertyOwnershipDetails(findMyHomeValuationForm),
        cbs_report_document: {
          ...cbsDocument,
          promo_code: cbsPromoCode || null,
          extracted_data: cbsExtractedData || null,
        },
      };

      const response = await fetch(
        "https://www.smart-lend.com/api/propTermLoan/createPropLoan",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + Cookies.get("token"),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            loan_data: JSON.stringify(loanPayload),
            requires_auth: false,
            application_session_id: progress.sessionId,
          }),
        },
      );

      const data = await response.json();

      if (response.status === 201) {
        progress.complete();
        setSubmissionStatus("not_started");
        setCbsUploadedDocument(null);
        setValuationStatus("not_started");
        setActiveTab("apply");
        setSubmissionSteps(4);
        requestAnimationFrame(() => applicationPageRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }));
        setFindMyHomeValuaionForm(createEmptyPropertyLoanForm());
        setSelectedLoanType(null);
        setAgreed(false);
        setPossibleResults(0);
        setCbsPromoCode("");
        setCbsFiles([]);
        setCbsFileText("");
        setCbsExtractedData(null);
        setRequestSelected(data.propTermLoanRequest?.id || null);
        message.success("Your property term loan application has been submitted.");
        await getMyPropLoans();
      } else {
        setSubmissionStatus("failed");
        message.error(data?.message || "Unable to submit loan request.");
      }
    } catch (error) {
      console.error(error);
      setSubmissionStatus("failed");
      message.error("Unable to submit loan request.");
    } finally {
      submissionInFlightRef.current = false;
      setCbsUploading(false);
    }
  };
  useEffect(() => {
    if (findMyHomeValuationForm.property) {
      setPossibleResults(0);
      startSeachResults(findMyHomeValuationForm.property);
    } else {
      valuationSearchRef.current++;
    }
  }, [findMyHomeValuationForm.property]);

  useEffect(() => {
    if (
      activeTab === "requested" &&
      propTermRequest.length > 0 &&
      !requestSelected
    ) {
      setRequestSelected(propTermRequest[0].id);
    }
  }, [activeTab, propTermRequest, requestSelected]);

  const cbsFileList = cbsFiles.map((file) => ({
    uid: file.uid || `${file.name}-${file.lastModified}`,
    name: file.name,
    status: "done",
  }));

  const renderProgressNotice = () => (
    <div className="pl-saved" role="status">
                      {progress.status === "retrying"
                        ? "Progress could not be saved yet. We’ll retry when connected."
                        : progress.status === "saving"
                          ? "Saving your application progress…"
                          : progress.status === "saved"
                            ? "✓ Your progress saves automatically. If you stop halfway, our team can help you finish."
                            : "Your progress saves automatically. If you stop halfway, our team can help you finish."}
                    </div>
  );

  const startApplication = () => {
    setSubmissionSteps(0);
    setActiveTab("apply");
    requestAnimationFrame(() => applicationPageRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }));
  };

  const renderRequestedTab = () => {
    if (!propTermRequest.length) {
      return <PropertyLoanEmptyState type="requested" onStart={startApplication} />;
    }

    return (
      <div>
        <div style={{ fontSize: "16px", fontWeight: 700 }}>
          Requested property term loans
        </div>
        <div style={{ marginTop: "6px", fontSize: "13px", opacity: 0.7 }}>
          Track applications and the offers you need to review.
        </div>
        {propTermRequest.length ? (
          <div className="loanCardsWrap" style={{ marginTop: 16 }}>
            {propTermRequest.map((pro) => {
              const address = pro.loan_data?.property?.address;
              const isHalfCaveat = Number(pro.loan_data?.selectedLoanType) === 2;
              const valuation = isHalfCaveat ? pro.loan_data?.estimatedPropertyValue : pro.loan_data?.valuation;
              const loanAmount = pro.loan_data?.loanAmount;
              const ltv = isHalfCaveat ? halfCaveatLtv(pro.loan_data) :
                valuation && pro.loan_data?.oustandingLoan !== undefined
                  ? ((pro.loan_data?.oustandingLoan + pro.loan_data?.cpfUsage) /
                      valuation) *
                    100
                  : null;
              return (
                <div
                  key={pro.id}
                  className={[
                    "slCard",
                    "loan-request-item",
                    "loan-hover-card",
                  ].join(" ")}
                  onClick={() => {
                    setRequestSelected(pro.id);
                  }}
                >
                  <div style={{ fontSize: "10px", opacity: 0.6 }}>
                    Loan Amount
                  </div>
                  <div>
                    {pro.loan_data?.loanAmount?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "SGD",
                    })}
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      opacity: 0.6,
                      marginTop: "10px",
                    }}
                  >
                    Property
                  </div>
                  <div>
                    {(address?.unit?.value !== undefined &&
                    address?.unit?.value !== null
                      ? `Unit ${address?.unit?.value}, `
                      : "") +
                      (address?.floor?.value !== undefined &&
                      address?.floor?.value !== null
                        ? `Floor ${address?.floor?.value}, `
                        : "") +
                      (address?.block?.value !== undefined &&
                      address?.block?.value !== null
                        ? `Block ${address?.block?.value}, `
                        : "") +
                      (address?.street?.value !== undefined
                        ? `${address?.street?.value}, `
                        : "") +
                      (address?.postal?.value !== undefined
                        ? `${address?.postal?.value}, `
                        : "") +
                      (address?.country?.desc !== undefined
                        ? `${address?.country?.desc}.`
                        : "")}
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      opacity: 0.6,
                      marginTop: "10px",
                    }}
                  >
                    Status
                  </div>
                  <div>{propertyMarketplaceLabel(pro)}</div>
                  <div className="slMetaGrid" style={{ marginTop: 12 }}>
                    <div
                      className="slMetaItem"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <div className="slMetaK">Property</div>
                      <div className="slMetaV">
                        {(address?.unit?.value !== undefined &&
                        address?.unit?.value !== null
                          ? `Unit ${address?.unit?.value}, `
                          : "") +
                          (address?.floor?.value !== undefined &&
                          address?.floor?.value !== null
                            ? `Floor ${address?.floor?.value}, `
                            : "") +
                          (address?.block?.value !== undefined &&
                          address?.block?.value !== null
                            ? `Block ${address?.block?.value}, `
                            : "") +
                          (address?.street?.value !== undefined
                            ? `${address?.street?.value}, `
                            : "") +
                          (address?.postal?.value !== undefined
                            ? `${address?.postal?.value}, `
                            : "") +
                          (address?.country?.desc !== undefined
                            ? `${address?.country?.desc}.`
                            : "")}
                      </div>
                    </div>
                    <div className="slMetaItem">
                      <div className="slMetaK">Property valuation</div>
                      <div className="slMetaV">{formatCurrency(valuation)}</div>
                    </div>
                    <div className="slMetaItem">
                      <div className="slMetaK">Property LTV</div>
                      <div className="slMetaV">
                        {ltv !== null ? `${ltv.toFixed(2)}%` : "—"}
                      </div>
                    </div>
                    <div className="slMetaItem">
                      <div className="slMetaK">Loan amount</div>
                      <div className="slMetaV">
                        {formatCurrency(loanAmount)}
                      </div>
                    </div>
                    <div className="slMetaItem">
                      <div className="slMetaK">Loan product</div>
                      <div className="slMetaV">{propertyLoanName(pro.loan_data?.selectedLoanType)}</div>
                      {isHalfCaveat && <div className="slMetaV">{pro.loan_data?.propertyShare}% share · {propertyLoanTenureLabel(2, pro.loan_data)}</div>}
                    </div>
                    <div className="slMetaItem">
                      <div className="slMetaK">Repayment type</div>
                      <div className="slMetaV">
                        {pro.loan_data?.loanPaymentType === "interest"
                          ? "Interest Only"
                          : "Interest and principal"}
                      </div>
                    </div>
                    <div className="slMetaItem sl-property-fee-detail">
                      <div className="slMetaK">Disbursement amount</div>
                      <div className="slMetaV">
                        {loanAmount ? formatCurrency(propertyLoanNetDisbursement(loanAmount)) : "—"}
                      </div>
                    </div>
                    <div className="slMetaItem sl-property-fee-detail">
                      <div className="slMetaK">Processing fee</div>
                      <div className="slMetaV">
                        {PROPERTY_LOAN_PROCESSING_FEE_LABEL} ({loanAmount ? formatCurrency(propertyLoanProcessingFee(loanAmount)) : "—"})
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "32px 20px",
              color: "rgba(10, 61, 98, 0.55)",
              fontSize: "13px",
            }}
          >
            <AccessTimeIcon
              style={{
                fontSize: 48,
                color: "rgba(10, 61, 98, 0.25)",
              }}
            />
            <div style={{ marginTop: "12px" }}>
              You haven’t submitted any property term loan applications yet.
            </div>
          </div>
        )}
        <div style={{ marginTop: "20px", fontWeight: 600, fontSize: "14px" }}>
          Offers to review
        </div>
        {activeLoan?.offers?.length ? (
          <div className="loanCardsWrap" style={{ marginTop: "16px" }}>
            {activeLoan.offers.map((offer) => renderOfferCard(offer))}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              color: "rgba(10, 61, 98, 0.55)",
              padding: "24px 20px",
              fontSize: "13px",
            }}
          >
            <AccessTimeIcon
              style={{
                fontSize: 40,
                color: "rgba(10, 61, 98, 0.25)",
              }}
            />
            <div style={{ marginTop: "10px" }}>
              {requestSelected
                ? "No offers to review right now."
                : "Select a loan request to view its offers."}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAcceptedTab = () => !awaitingResponseOffers.length
    ? <PropertyLoanEmptyState type="accepted" onStart={startApplication} />
    : (
    <div>
      <div className="loanApplyHeaderRow">
        <div>
          <div className="loanApplyTitle">Accepted offers</div>
          <div className="loanApplyHint">
            Track offers you've accepted and are awaiting lender response on.
          </div>
        </div>
        <div className="loanApplyPill">Awaiting response</div>
      </div>
      <div className="loanDivider" />
      {awaitingResponseOffers.length ? (
        <div className="loanCardsWrap" style={{ marginTop: 16 }}>
          {awaitingResponseOffers.map((offer) => renderOfferCard(offer))}
        </div>
      ) : (
        null
      )}
    </div>
  );

  const renderOngoingTab = () => !ongoingOffers.length
    ? <PropertyLoanEmptyState type="ongoing" onStart={startApplication} />
    : (
    <div>
      <div className="loanApplyHeaderRow">
        <div>
          <div className="loanApplyTitle">Ongoing loans</div>
          <div className="loanApplyHint">
            Review your active loans and upcoming installments.
          </div>
        </div>
        <div className="loanApplyPill">Repayment tracker</div>
      </div>
      <div className="loanDivider" />
      {ongoingOffers.length ? (
        <div className="loanCardsWrap" style={{ marginTop: 16 }}>
          {ongoingOffers.map((offer) => {
            const schedules =
              offer.repaymentSchedules || offer.repayment_schedules || [];
            const statusDisplay = getStatusDisplay(offer.status);
            const disbursementReached = isDisbursementReached(offer);
            const statusLabel =
              offer.status === "pending_disbursement" && disbursementReached
                ? "Ongoing"
                : statusDisplay.label || "Ongoing";
            const statusColor =
              offer.status === "pending_disbursement" && disbursementReached
                ? "green"
                : statusDisplay.color;
            return (
              <div
                key={offer.id}
                className={[
                  "slCard",
                  "loan-hover-card",
                  isMobile ? "isMobile" : "",
                ].join(" ")}
                style={{ marginTop: 16, color: "rgba(10, 61, 98, 0.95)" }}
              >
                <div className="slTopRow">
                  <div className="slPills">
                    <span
                      className={["slPill", getPillVariant(statusColor)].join(
                        " ",
                      )}
                    >
                      {statusLabel}
                    </span>
                    <span className="slPill">Offer #{offer.id}</span>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "8px",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: 600 }}>
                      {offer.lender?.entity_name ||
                        offer.lender?.principal_name ||
                        "Lender"}
                    </div>
                    <div className="slSubLine">
                      Updated {formatDateTime(offer.updated_at)}
                    </div>
                  </div>
                </div>
                <div className="slMetaGrid" style={{ marginTop: 12 }}>
                  <div className="slMetaItem">
                    <div className="slMetaK">Loan amount</div>
                    <div className="slMetaV">
                      {formatCurrency(
                        offer.offer_data?.amount ||
                          offer.offer_data?.amount_offer,
                      )}
                    </div>
                  </div>
                  <div className="slMetaItem">
                    <div className="slMetaK">Tenor</div>
                    <div className="slMetaV">
                      {formatYears(
                        offer.offer_data?.tenor ||
                          offer.offer_data?.tenor_years,
                      )}
                    </div>
                  </div>
                  <div className="slMetaItem sl-property-fee-detail">
                    <div className="slMetaK">Processing fee</div>
                    <div className="slMetaV">
                      {PROPERTY_LOAN_PROCESSING_FEE_LABEL} ({formatCurrency(propertyLoanProcessingFee(offer.offer_data?.amount ?? offer.offer_data?.amount_offer))})
                    </div>
                  </div>
                  <div className="slMetaItem sl-property-fee-detail">
                    <div className="slMetaK">Disbursement amount</div>
                    <div className="slMetaV">
                      {formatCurrency(propertyLoanNetDisbursement(offer.offer_data?.amount ?? offer.offer_data?.amount_offer))}
                    </div>
                  </div>
                  <div className="slMetaItem" style={{ gridColumn: "1 / -1" }}>
                    <div className="slMetaK">Next repayment</div>
                    <div className="slMetaV">
                      {offer.next_repayment
                        ? `${formatDate(
                            offer.next_repayment.due_date,
                          )} · ${formatCurrency(
                            offer.next_repayment.amount_due,
                          )}`
                        : "Not scheduled yet"}
                    </div>
                  </div>
                  {getDisbursementDate(offer) && (
                    <div
                      className="slMetaItem"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <div className="slMetaK">Disbursement date</div>
                      <div className="slMetaV">
                        {formatDate(getDisbursementDate(offer))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="property-term-repayment-actions">
                  <Button
                    className="slBtn slBtnPrimary"
                    type="primary"
                    onClick={() => setRepaymentScheduleOffer(offer)}
                    disabled={!schedules.length}
                  >
                    View repayment schedule
                  </Button>
                  {!schedules.length && (
                    <div className="property-term-repayment-empty">
                      Repayment schedule has not been provided yet.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        null
      )}
    </div>
  );

  return (
    <div className="property-term-loan-page property-loan-redesign">
      <div className="property-term-loan-shell">
        <Modal
          open={Boolean(repaymentScheduleOffer)}
          onCancel={() => setRepaymentScheduleOffer(null)}
          footer={null}
          width={720}
        >
          <div className="property-term-repayment-modal">
            <div className="property-term-repayment-modal__title">
              Repayment schedule
            </div>
            <div className="property-term-repayment-modal__subtitle">
              {repaymentScheduleOffer?.lender?.entity_name ||
                repaymentScheduleOffer?.lender?.principal_name ||
                "Lender"}
            </div>
            <div className="property-term-repayment-table">
              <div className="property-term-repayment-row property-term-repayment-head">
                <div>Due date</div>
                <div>Principal</div>
                <div>Interest</div>
                <div>Total</div>
                <div>Status</div>
              </div>
              {(
                repaymentScheduleOffer?.repaymentSchedules ||
                repaymentScheduleOffer?.repayment_schedules ||
                []
              ).length ? (
                (
                  repaymentScheduleOffer?.repaymentSchedules ||
                  repaymentScheduleOffer?.repayment_schedules ||
                  []
                ).map((repayment) => (
                  <div
                    key={repayment.id || repayment.due_date}
                    className="property-term-repayment-row"
                  >
                    <div>{formatDate(repayment.due_date)}</div>
                    <div>{formatCurrency(repayment.principal)}</div>
                    <div>{formatCurrency(repayment.interest)}</div>
                    <div>{formatCurrency(repayment.total_amount)}</div>
                    <div>{repaymentStatusLabel(repayment)}</div>
                  </div>
                ))
              ) : (
                <div className="property-term-repayment-empty">
                  Repayment schedule has not been provided yet.
                </div>
              )}
            </div>
          </div>
        </Modal>
        <div className="property-term-loan-layout">
          <main className="property-term-loan-main" ref={applicationPageRef}>
            <div className="loanPageCard property-term-application-shell">
              <div
                className="loanPageHeader"
                style={{ borderBottom: "1px solid rgba(20, 44, 68, 0.08)" }}
              >
                <p className="loanTitle">Property Pledged Loan</p>
                <div className="loanSubtitle">
                  Unlock the value in your private property. Compare offers from multiple lenders.
                </div>
                <a className="pl-header-help" href="#property-loan-faq">Need help? View FAQs ↗</a>
              </div>
              <div className="loan-tabs-wrap">
                <Tabs
                  className="loan-tabs"
                  activeKey={activeTab}
                  onChange={(key) => setActiveTab(key)}
                >
                  <TabPane
                    tab="Apply loan"
                    key="apply"
                  >
                    {renderProgressNotice()}

                    <PropertyLoanApplication
                      form={findMyHomeValuationForm}
                      setForm={setFindMyHomeValuaionForm}
                      step={submissionSteps}
                      setStep={setSubmissionSteps}
                      properties={selectableProperties}
                      myinfoNotification={myinfoNotification}
                      userDetails={userDetails}
                      result={possibleResults}
                      loading={loadingResults}
                      valuationStatus={valuationStatus}
                      retryValuation={() => startSeachResults(findMyHomeValuationForm.property)}
                      agreed={agreed}
                      setAgreed={setAgreed}
                      selectedLoanType={selectedLoanType}
                      setSelectedLoanType={setSelectedLoanType}
                      typesOfLoans={typesOfLoans}
                      uploadDocument={UniversaleUploadFile}
                      cbsPromoCode={cbsPromoCode}
                      refreshCbsCode={fetchCbsPromoCode}
                      cbsFiles={cbsFiles}
                      cbsFileList={cbsFileList}
                      beforeCbsUpload={handleCbsBeforeUpload}
                      removeCbs={() => {
                        setCbsFiles([]);
                        setCbsFileText("");
                        setCbsExtractedData(null);
                        setCbsUploadedDocument(null);
                      }}
                      cbsExtractedData={cbsExtractedData}
                      submitting={cbsUploading}
                      submit={createPropLoan}
                      onTrackRequested={() => setActiveTab("requested")}
                      updateRepaymentSchedule={updateRepaymentSchedule}
                      trackLoanAmount={(value) => sendTrackingUpdate({ enteredLoanAmount: true, loanAmount: value ?? null })}
                    />
                  </TabPane>
                  <TabPane tab={<span>Requested Loans <span className="pl-tab-count">{propTermRequest.length}</span></span>} key="requested">
                    {renderProgressNotice()}
                    {renderRequestedTab()}
                  </TabPane>
                  <TabPane tab={<span>Accepted Offers <span className="pl-tab-count">{awaitingResponseOffers.length}</span></span>} key="accepted">
                    {renderProgressNotice()}
                    {renderAcceptedTab()}
                  </TabPane>
                  <TabPane tab={<span>Ongoing Loans <span className="pl-tab-count">{ongoingOffers.length}</span></span>} key="ongoing">
                    {renderProgressNotice()}
                    {renderOngoingTab()}
                  </TabPane>
                </Tabs>
              </div>
              <PropertyLoanFaq />
            </div>
          </main>
        </div>
        <Modal
          open={offerActionModal.open}
          title={
            OFFER_ACTION_COPY[offerActionModal.action]?.title ||
            "Respond to offer"
          }
          okText={
            OFFER_ACTION_COPY[offerActionModal.action]?.okText || "Submit"
          }
          onCancel={closeOfferActionModal}
          onOk={handleOfferActionSubmit}
          confirmLoading={offerActionSubmitting}
          destroyOnClose
        >
          <div
            style={{
              fontSize: "13px",
              color: "var(--br-text, rgba(20, 44, 68, 0.8))",
            }}
          >
            {OFFER_ACTION_COPY[offerActionModal.action]?.description}
          </div>
          <Input.TextArea
            rows={3}
            style={{ marginTop: "12px" }}
            value={offerActionMessage}
            onChange={(event) => setOfferActionMessage(event.target.value)}
            maxLength={600}
            placeholder={
              offerActionModal.action === "renegotiate"
                ? "Let the lender know what you'd like to adjust."
                : "Optional note for the lender"
            }
          />
        </Modal>
      </div>
    </div>
  );
};

export default PropertyTermLoan;
